import { IconName, MinimalIconProps } from '@ardoq/icons';
import { StatusType } from '../types';
import { RequireAtLeastOne } from '@ardoq/common-helpers';
import { UnstyledLinkProps } from '@ardoq/link';
import type { I18nLocale } from '@ardoq/i18n';

export type BetaBadgeProperties =
  | ReadonlyBetaBadgeProperties
  | InteractiveBetaBadgeProperties;

type ReadonlyBetaBadgeProperties = Record<never, never>;

export const BetaBadgeOptionKey = {
  KNOWLEDGE_BASE: 'knowledgeBase',
  SHARE_FEEDBACK: 'shareFeedback',
  CHANGELOG: 'changelog',
  VIEW_INTRODUCTION: 'viewIntroduction',
  TAKE_THE_TOUR: 'takeTour',
} as const;

export type BetaBadgeOptionKey =
  (typeof BetaBadgeOptionKey)[keyof typeof BetaBadgeOptionKey];

export type InteractiveBetaBadgeProperties = RequireAtLeastOne<
  {
    trackEvent?: (trackingString: string) => void;
    knowledgeBaseUrl: string;
    shareFeedbackUrl: string;
    changelogUrl: string;
    openOnboardingModal: VoidFunction;
    onTakeTour: VoidFunction;
  },
  | 'knowledgeBaseUrl'
  | 'shareFeedbackUrl'
  | 'changelogUrl'
  | 'openOnboardingModal'
  | 'onTakeTour'
> & {
  i18nLocale?: I18nLocale;
  optionsOrder?: BetaBadgeOptionKey[];
  labelOverrides?: Partial<
    Record<BetaBadgeOptionKey, { i18nKey: string; defaultValue: string }>
  >;
};

export type NewBadgeProperties = {
  label?: string;
  i18nLocale?: I18nLocale;
};

export type DefaultOrScenarioBadgeProperties = {
  statusType: StatusType.SCENARIO | StatusType.DEFAULT;
  iconName?: IconName;
  label: string;
  'data-tooltip-text'?: string;
};

export type ReadonlyBadgeProps = {
  statusType:
    | StatusType.DEFAULT
    | StatusType.ERROR
    | StatusType.WARNING
    | StatusType.SUCCESS
    | StatusType.INFO
    | StatusType.SCENARIO
    | StatusType.NEW
    | StatusType.BETA
    | StatusType.TRIAL
    | StatusType.WORKSPACE
    | StatusType.SUBTLE;
  label: string;
  iconName?: IconName;
  'data-tooltip-text'?: string;
};

type BadgeDropdownButton = {
  type: 'button';
  label: string;
  leftIcon?: MinimalIconProps;
  onClick: VoidFunction;
};

type BadgeDropdownLink = {
  type: 'link';
  label: string;
  leftIcon?: MinimalIconProps;
} & Pick<UnstyledLinkProps, 'href' | 'target'>;

export type BadgeDropdownItem = BadgeDropdownButton | BadgeDropdownLink;

/**
 * Prefer using {@link toInteractiveBadgeProps} to create these props
 * rather than constructing the object directly.
 */
export type InteractiveBadgeProps = {
  type: 'interactive';
  statusType: StatusType.BETA | StatusType.NEW | StatusType.TRIAL;
  options: BadgeDropdownItem[];
};

export type BadgeProperties =
  | ReadonlyBadgeProps
  | DefaultOrScenarioBadgeProperties
  | InteractiveBadgeProps;
